// import axios from 'axios';
import router from '@/router';
import Vue from 'vue';

import firebase from 'firebase/app'
import 'firebase/auth'

import { vuexfireMutations, firestoreAction } from 'vuexfire';
import { db, references } from '@/main'

import { ToastProgrammatic as Toast } from 'buefy'

const state = () => ({
  user: null,
  logged_in_uid: null,
  practice: null,
  isLoading: false, // fullpage
  isLoadingSmall: false, // not fullpage
  isReady: false, // practice has been fetched, everything all good
})
const defaultState = { // to reset store at logout
  user: null,
  logged_in_uid: null,
  practice: null,
  isLoading: false,
  isLoadingSmall: false,
  isReady: false,
}

const getters = {
  getUsers(state) {
    return state.practice.users
  }
}

const mutations = {
  RESET(state) {
    Object.keys(defaultState).forEach(key => {
      state[key] = defaultState[key];
    });
  },
  SET_USER_UID(state, uid) {
    state.logged_in_uid = uid;
  },
  SET_PRACTICE(state, practice) {
    state.practice = practice;
  },
  NO_MORE_TRIAL(state) {
    // disable stripe eligibility locally
    state.user.subscription.eligible_for_trial = false;
  },
  // is this even being called anymore???
  SET_PFP(state, { idx, property, payload }) {
    Vue.set(state.practice.users[idx], property, payload)
    // state.practice.users[idx][property] = payload
  },
  IS_READY(state, value) {
    state.isReady = value
  },
  SET_LOADING(state, value) {
    state.isLoading = value;
  },
  SET_LOADING_SMALL(state, value) {
    state.isLoadingSmall = value;
  },
  ...vuexfireMutations,
}

const actions = {
  login: firestoreAction(async({ state, dispatch, commit, bindFirestoreRef }, { email, password }) => {
    try {
      commit('SET_LOADING', true)
      // login, bind user data
      const { user } = await firebase.auth().signInWithEmailAndPassword(email, password)
      commit('SET_USER_UID', user.uid);
      await bindFirestoreRef('user', db.collection(references.user).doc(user.uid))
      // increment login_count
      await db.collection(references.user).doc(user.uid).update({
        login_count: state.user.login_count + 1
      })
      // get all our content, using the old store's actions. 
      // firstBind is different from the onAuthState observer in main.js's mounted hook, 
      // since we only need to fetch the static content (videos, default playlists) once, not on every refresh.
      dispatch('firstBind', {}, { root: true })
      // next page
      router.push('/patients')
    } catch(err) {
      console.log(err)
      const message = err.message || 'Could not sign in. Try again or contact support.'
      Toast.open({
        duration: 5000,
        position: 'is-bottom',
        type: 'is-danger',
        message
      })
    } finally {
      commit('SET_LOADING', false)
    }
  }),
  fetchPractice: firestoreAction(async({ commit, dispatch }, { user }) => { // called on auth/login and App.vue mounted()
    // setup live listening for changes to the practice
    db.collection(references.practice).doc(user.practice.id).onSnapshot(async(snap) => {
      // onSnapshot mixed with commit() lets us watch for changes live, while committing them to the store. Not possible with .get() and not reliable with bindFirestoreRef
      const practice = snap.data();
      commit('SET_PRACTICE', practice);
      await dispatch('stripe/fetchStripeStatus', practice, { root: true });
      
      // check for a success message from stripe payment
      // if payment is successful, update the customer's trial eligibility
      const stripeMsg = router.app._route.query.msg;
      if (stripeMsg && stripeMsg === 'stripe_success' && user.subscription.eligible_for_trial) {
        await dispatch('stripe/disableTrialEligibility', practice, { root: true });
        commit('NO_MORE_TRIAL');
      }

      // since this is the last step run when logging in or refreshing the page, we we trigger an $emit('ready') on root from main.js
      // right now $on('ready') isn't even being used anywhere - it's only commented out for now. 
      // but the value here still IS being used
      commit('IS_READY', true);
    });
    return
  }),

  logout: async({ commit }) => {
    try {
      await firebase.auth().signOut();
      commit('RESET');
      router.push('/log-in');
    } catch(err) {
      console.log(err);
    }
  },

  register: async({ commit, dispatch }, { email, password, practice_name, first, last, is_owner, existingPracticeID, title }) => { // register your account, or your practice
    // if is_owner is false, we expect a practice_id
    // if is_owner is true, we expect a practice name
    try {
      commit('SET_LOADING', true)
      // create user account
      const { user } = await firebase.auth().createUserWithEmailAndPassword(email, password) // we only want the user property, so deconstruct the returned object
      if (is_owner) {
        // create practice id
        const practice_id = user.uid.substr(10, 8).toUpperCase() // random 8 digits from the middle of a user id
        // create user doc
        await db.collection(references.user).doc(user.uid).set({
          login_count: 0,
          role: 'OWNER',
          email: user.email.toLowerCase(),
          name: { first, last },
          practice: {
            id: practice_id
          },
          subscription: {
            eligible_for_trial: true,
          },
          facebook_images: {},
        })
        // create practice doc
        await db.collection(references.practice).doc(practice_id).set({
          practice_logo: null,
          video_intro: null,
          video_outro: null,
          owner: {
            uid: user.uid,
            name: { first, last },
            email: user.email.toLowerCase()
          },
          users: [
            { name: { first, last },
              role: 'OWNER',
              status: 'Joined',
              uid: user.uid,
              profile_pic: null,
              email
            }
          ],
          practice_name,
          practice_id,
        })
      } else { // for non owner registration
        // create user doc
        await db.collection(references.user).doc(user.uid).set({
          login_count: 0,
          role: 'PT',
          email: user.email.toLowerCase(),
          name: { first, last },
          practice: {
            id: existingPracticeID
          },
          title,
        })
        // update the practice's "users" array with a status of 'Joined'
        let users = [];
        await db.collection(references.practice).doc(existingPracticeID).get() // fetch the array
          .then(snap => {
            users = snap.data().users
          })
        const me = users.find(obj => obj.email.toLowerCase() === email.toLowerCase()) // find the current user in the users array
        users[users.indexOf(me)].status = 'Joined'
        users[users.indexOf(me)].uid = user.uid
        await db.collection(references.practice).doc(existingPracticeID).update({ users })
      }
      // finally, login
      dispatch('login', { email, password })
    } catch(err) {
      const message = err.message || 'Could not register. Try again or contact support.'
      Toast.open({
        duration: 5000,
        position: 'is-bottom',
        type: 'is-danger',
        message
    })
      console.dir(err)
      commit('SET_LOADING', false)
    } 
    // do not disable loading on success, since we dispatch login, which will take care of loading stating
  },
  inviteUser: async({ state, commit }, employee) => { // invite employees to your practice
    try {
      commit('SET_LOADING_SMALL', true);
      // invite user to the platform
      const url = `https://aceso-recovery.netlify.app/register?e=${employee.email}&p_id=${state.practice.practice_id}&fn=${employee.name.first}&ln=${employee.name.last}&t=${employee.title}`;
      // const url = `https://localhost:8085/register?e=${employee.email}&p_id=${state.practice.practice_id}&fn=${employee.name.first}&ln=${employee.name.last}`
      console.log(url)
      await db.collection("mailingList").add({
        to: employee.email,
        message: {
          subject: `Welcome to Aceso Recovery`,
          html: `<head> <meta name="color-scheme" content="light dark"> </head>
                  <div style="background-color: whitesmoke; width: 100%; height: auto; padding-bottom: 25px">
                  <h1 style="color: #222222; text-align: center; font-family: Tahoma, Verdana, sans-serif; padding-top: 25px">Welcome!</h1>
                  <h1 style="color: #222222; text-align: center; font-family: Tahoma, Verdana, sans-serif; padding-top: 5px">${employee.name.first} ${employee.name.last}</h1>
                  <h3 style="font-family: Tahoma, Verdana, sans-serif; color: #222222; text-align: center;">${state.practice.owner.name.first} ${state.practice.owner.name.last} at ${state.practice.practice_name} has invited you to Aceso Recovery.</h3> 
                  <div style="display: flex; justify-content: center; margin: auto; width: auto; height: auto; align-content: center ">
                    <a style="border-radius: 25px; padding-top: 12px; padding-bottom: 12px; padding-left: 18px; padding-right: 18px; background-color: #18a0fb; font-family: Tahoma, Verdana, sans-serif; color: white !important; text-decoration: none; " href='${url}'>Join Now</a> </div>
                    <p style="font-family: Tahoma, Verdana, sans-serif; color: #222222; text-align: center;">If the link above does not work, you can manually enter your practice code:</p>
                    <h4 style="font-family: Tahoma, Verdana, sans-serif; color: #222222; text-align: center;">${state.practice.practice_id}</h4>
                  </div>`
        },
      })
      // add to the practice's "users" array
      employee.profile_pic = null
      await db.collection(references.practice).doc(state.user.practice.id).update({
        users: firebase.firestore.FieldValue.arrayUnion(employee)
      })
    } catch(err) {
      const message = err.message || 'Could not invite user. Try again or contact support.'
      Toast.open({
        duration: 5000,
        position: 'is-bottom',
        type: 'is-danger',
        message
      })
    } finally {
      commit('SET_LOADING_SMALL', false)
    }
  },
  deleteUser: async({ commit, state }, { email, uid }) => { // remove a user from your practice
    try {
      commit('SET_LOADING_SMALL', true)
      // remove from practice "users" array
      let users = [];
      await db.collection(references.practice).doc(state.practice.practice_id).get() // fetch the array
        .then(snap => {
          users = snap.data().users
        })
      const me = users.find(obj => obj.email.toLowerCase() === email.toLowerCase()) // find the current user
      users.splice(users.indexOf(me), 1)
      await db.collection(references.practice).doc(state.practice.practice_id).update({ users })
      // delete user doc from references.user
      await db.collection(references.user).doc(uid).delete()
      // delete user account
      // TODO: make an api call to delete a user id
      Toast.open('Deleted user!')
    } catch(err) {
      console.log(err)
    } finally {
      commit('SET_LOADING_SMALL', false)
    }
  },

  editUser: async({ commit, state }, { name, title, email }) => { // remove a user from your practice
    try {
      commit('SET_LOADING_SMALL', true)
      // fetch the practice's users array
      let users = [];
      await db.collection(references.practice).doc(state.practice.practice_id).get() 
        .then(snap => {
          users = snap.data().users
        })
      const me = users.find(obj => obj.email.toLowerCase() === email.toLowerCase()) // find the current user
      const userIdx = users.indexOf(me);
      users[userIdx].name = name;
      users[userIdx].title = title;
      // update in users doc
      await db.collection(references.user).doc(users[userIdx].uid).update({ name, title });
      // update in practice's users array
      await db.collection(references.practice).doc(state.practice.practice_id).update({ users });

      Toast.open('Saved changes')
    } catch(err) {
      console.log(err)
      Toast.open({
        message: 'Failed to save',
        type: 'is-danger'
      })
    } finally {
      commit('SET_LOADING_SMALL', false)
    }
  },

  unbindAllFirestore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('practice');
    unbindFirestoreRef('user');
    sessionStorage.clear();
  }),
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}